import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import {
  BotOff,
  ChevronDown,
  Gem,
  Menu,
  Scale,
  type LucideIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { t } from "@/lib/i18n";
import UnicornScene from "unicornstudio-react";
import { RetroGrid } from "@/components/ui/retro-grid";
import { HyperText } from "@/components/ui/hyper-text";
import { FlickeringGrid } from "@/components/ui/flickering-grid";

export const Route = createFileRoute("/landing-page/")({
  component: LandingPage,
});

const DOCS_URL =
  "https://crypto-fantasy-league-1.gitbook.io/crypto-fantasy-league-docs";
const X_URL = "https://x.com/cfldotfun";
const DISCORD_URL = "https://discord.com/invite/GQuP7ekfF9";
const CFL_LOGO_URL = "https://cdn.navalabs.io/logo-abbreviated_dznpoc.png";

const MODE_IMAGES = {
  winnerTakesAll: "/assets/landing-page/wta-card.svg",
  monthlyCup: "/assets/landing-page/championship-card.svg",
  rush: "/assets/landing-page/rush-card.svg",
  kingOfTheHill: "/assets/landing-page/koth-card.svg",
} as const;

const DEFAULT_VIEWPORT = {
  once: true,
  amount: 0.2,
} as const;

const CONNECTOR_RECTS = [
  { x: 2, y: 1, sequence: 0 },
  { x: 2, y: 5, sequence: 0 },
  { x: 3, y: 2, sequence: 1 },
  { x: 3, y: 4, sequence: 1 },
  { x: 4, y: 3, sequence: 2 },
] as const;

const CONNECTOR_SEQUENCE_DURATION = 0.75;
const CONNECTOR_SEQUENCE_BASE = 0.08;
const CONNECTOR_SEQUENCE_STEP = 0.28;
const CONNECTOR_SEQUENCE_FADE_IN = 0.08;
const CONNECTOR_SEQUENCE_HOLD = 0.18;

type RevealOptions = {
  y?: number;
  scale?: number;
  duration?: number;
  delay?: number;
};

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

function createRevealVariant(
  prefersReducedMotion: boolean,
  options: RevealOptions = {},
): Variants {
  const { y = 20, scale = 1, duration = 0.6, delay = 0 } = options;

  return {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : y,
      scale: prefersReducedMotion ? 1 : scale,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: prefersReducedMotion ? 0.01 : duration,
        delay: prefersReducedMotion ? 0 : delay,
        ease: EASE_OUT,
      },
    },
  };
}

function createStaggerVariant(
  prefersReducedMotion: boolean,
  staggerChildren = 0.12,
  delayChildren = 0,
): Variants {
  return {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : staggerChildren,
        delayChildren: prefersReducedMotion ? 0 : delayChildren,
      },
    },
  };
}

function createConnectorRectVariants(prefersReducedMotion: boolean): Variants {
  if (prefersReducedMotion) {
    return {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          duration: 0.01,
        },
      },
    };
  }

  return {
    hidden: { opacity: 50 },
    visible: (sequence: number) => {
      const start =
        CONNECTOR_SEQUENCE_BASE + sequence * CONNECTOR_SEQUENCE_STEP;
      const fadeIn = Math.min(start + CONNECTOR_SEQUENCE_FADE_IN, 0.96);
      const hold = Math.min(start + CONNECTOR_SEQUENCE_HOLD, 0.98);

      return {
        opacity: [0.5, 0.5, 1, 1, 0.5],
        transition: {
          duration: CONNECTOR_SEQUENCE_DURATION,
          times: [0, start, fadeIn, hold, 1],
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        },
      };
    },
  };
}

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion() ?? false;

  const navVariants = createRevealVariant(prefersReducedMotion, {
    y: 14,
    duration: 0.45,
  });
  const heroGroupVariants = createStaggerVariant(
    prefersReducedMotion,
    0.12,
    0.1,
  );
  const heroLineVariants = createRevealVariant(prefersReducedMotion, {
    y: 30,
    duration: 0.7,
  });
  const badgeVariants = createRevealVariant(prefersReducedMotion, {
    y: 18,
    duration: 0.45,
  });
  const headingGroupVariants = createStaggerVariant(
    prefersReducedMotion,
    0.1,
    0.04,
  );
  const headingVariants = createRevealVariant(prefersReducedMotion, {
    y: 24,
    duration: 0.55,
  });
  const bodyVariants = createRevealVariant(prefersReducedMotion, {
    y: 14,
    duration: 0.45,
  });
  const buttonGroupVariants = createStaggerVariant(
    prefersReducedMotion,
    0.1,
    0.08,
  );
  const buttonVariants = createRevealVariant(prefersReducedMotion, {
    y: 16,
    duration: 0.45,
  });
  const sectionGridVariants = createStaggerVariant(
    prefersReducedMotion,
    0.12,
    0.08,
  );
  const cardVariants = createRevealVariant(prefersReducedMotion, {
    y: 28,
    scale: 0.98,
    duration: 0.6,
  });
  const cardContentVariants = createStaggerVariant(
    prefersReducedMotion,
    0.08,
    0.05,
  );
  const connectorVariants = createRevealVariant(prefersReducedMotion, {
    y: 0,
    duration: 0.4,
    delay: 0.12,
  });
  const connectorRectVariants =
    createConnectorRectVariants(prefersReducedMotion);
  const imageVariants = createRevealVariant(prefersReducedMotion, {
    y: 18,
    scale: 1.03,
    duration: 0.55,
  });
  const footerColumnVariants = createRevealVariant(prefersReducedMotion, {
    y: 18,
    duration: 0.45,
  });

  const socialLinks = [
    { name: t("landingPage.socialLinks.xTwitter"), url: X_URL },
    { name: t("landingPage.socialLinks.discord"), url: DISCORD_URL },
  ];

  const stats = [
    {
      value: t("landingPage.stats.totalPlayers.value"),
      label: t("landingPage.stats.totalPlayers.label"),
    },
    {
      value: t("landingPage.stats.matchesPlayed.value"),
      label: t("landingPage.stats.matchesPlayed.label"),
    },
    {
      value: t("landingPage.stats.monthlyReward.value"),
      label: t("landingPage.stats.monthlyReward.label"),
    },
    {
      value: t("landingPage.stats.totalVolume.value"),
      label: t("landingPage.stats.totalVolume.label"),
    },
  ];

  const howToPlayCards = [
    {
      id: 1,
      title: t("landingPage.howToPlay.steps.pickTokens.title"),
      description: t("landingPage.howToPlay.steps.pickTokens.description"),
      borderClass:
        "border border-[#d8d8d0] shadow-[0_0_18px_rgba(132,204,22,0.2)]",
    },
    {
      id: 2,
      title: t("landingPage.howToPlay.steps.compete.title"),
      description: t("landingPage.howToPlay.steps.compete.description"),
      borderClass:
        "border border-[#d8d8d0] shadow-[0_0_18px_rgba(132,204,22,0.2)]",
    },
    {
      id: 3,
      title: t("landingPage.howToPlay.steps.winRewards.title"),
      description: t("landingPage.howToPlay.steps.winRewards.description"),
      borderClass:
        "border border-[#d8d8d0] shadow-[0_0_18px_rgba(132,204,22,0.2)]",
    },
  ];

  const modes = [
    {
      image: MODE_IMAGES.winnerTakesAll,
      imageAlt: t("landingPage.gameModes.cards.winnerTakesAll.imageAlt"),
      title: t("landingPage.gameModes.cards.winnerTakesAll.title"),
      description: t("landingPage.gameModes.cards.winnerTakesAll.description"),
      cardClass:
        "bg-white border-[0.5px] border-[#22C55F] shadow-[0_0px_12px_rgba(34,197,95,20%)]",
    },
    {
      image: MODE_IMAGES.monthlyCup,
      imageAlt: t("landingPage.gameModes.cards.monthlyCup.imageAlt"),
      title: t("landingPage.gameModes.cards.monthlyCup.title"),
      description: t("landingPage.gameModes.cards.monthlyCup.description"),
      cardClass:
        "bg-white border-[0.5px] border-[#22C55F] shadow-[0_0px_16px_rgba(34,197,95,12%)]",
    },
    {
      image: MODE_IMAGES.rush,
      imageAlt: t("landingPage.gameModes.cards.rush.imageAlt"),
      title: t("landingPage.gameModes.cards.rush.title"),
      description: t("landingPage.gameModes.cards.rush.description"),
      cardClass:
        "bg-white border-[0.5px] border-[#22C55F] shadow-[0_0px_16px_rgba(34,197,95,12%)]",
    },
    {
      image: MODE_IMAGES.kingOfTheHill,
      imageAlt: t("landingPage.gameModes.cards.kingOfTheHill.imageAlt"),
      title: t("landingPage.gameModes.cards.kingOfTheHill.title"),
      description: t("landingPage.gameModes.cards.kingOfTheHill.description"),
      cardClass:
        "bg-white border-[0.5px] border-[#22C55F] shadow-[0_0px_16px_rgba(34,197,95,12%)]",
    },
  ];

  const whyCflFeatures: {
    icon: LucideIcon;
    // icon: ReactElement;
    title: string;
    description: string;
  }[] = [
    {
      icon: Gem,
      // icon: (
      //   <svg
      //     xmlns="http://www.w3.org/2000/svg"
      //     viewBox="0 0 24 24"
      //     className="size-7 text-[#22C55E]"
      //   >
      //     <polygon
      //       fill="currentColor"
      //       points="17 9 17 11 16 11 16 12 15 12 15 13 14 13 14 14 13 14 13 15 12 15 12 16 10 16 10 15 9 15 9 14 8 14 8 13 7 13 7 11 8 11 8 10 10 10 10 11 12 11 12 10 13 10 13 9 14 9 14 8 16 8 16 9 17 9"
      //     />
      //     <path
      //       fill="currentColor"
      //       d="m22,8v-1h-1v-1h-1v-1h-1v-1h-1v-1h-1v-1h-1v-1h-8v1h-1v1h-1v1h-1v1h-1v1h-1v1h-1v1h-1v8h1v1h1v1h1v1h1v1h1v1h1v1h1v1h8v-1h1v-1h1v-1h1v-1h1v-1h1v-1h1v-1h1v-8h-1Zm-1,7h-1v1h-1v1h-1v1h-1v1h-1v1h-1v1h-6v-1h-1v-1h-1v-1h-1v-1h-1v-1h-1v-1h-1v-6h1v-1h1v-1h1v-1h1v-1h1v-1h1v-1h6v1h1v1h1v1h1v1h1v1h1v1h1v6Z"
      //     />
      //   </svg>
      // ),
      title: t("landingPage.whyCfl.cards.realPrize.title"),
      description: t("landingPage.whyCfl.cards.realPrize.description"),
    },
    {
      icon: Scale,
      title: t("landingPage.whyCfl.cards.evenPlayingField.title"),
      description: t("landingPage.whyCfl.cards.evenPlayingField.description"),
    },
    {
      icon: BotOff,
      title: t("landingPage.whyCfl.cards.humansOnly.title"),
      description: t("landingPage.whyCfl.cards.humansOnly.description"),
    },
  ];

  return (
    <div
      className="relative bg-white text-[#111827]"
      style={{
        fontFamily: "'DeadRevolver', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "none",
      }}
    >
      <header className="absolute top-0 z-20 w-full">
        <div className="relative mx-auto flex w-full max-w-[1440px] items-center justify-between px-4 py-5 sm:px-8 md:px-12 lg:px-20">
          <motion.div initial="hidden" animate="visible" variants={navVariants}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-green-950/65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              aria-label={t("landingPage.header.logoAriaLabel")}
            >
              <img
                src={CFL_LOGO_URL}
                alt={t("landingPage.cflLogo")}
                className="h-7 w-auto object-contain sm:h-8"
              />
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={navVariants}
            className="hidden items-center gap-8 md:flex"
          >
            <div
              className="relative"
              onMouseEnter={() => setSocialsOpen(true)}
              onMouseLeave={() => setSocialsOpen(false)}
              onFocusCapture={() => setSocialsOpen(true)}
              onBlurCapture={(event) => {
                const relatedTarget = event.relatedTarget as Node | null;
                if (
                  !relatedTarget ||
                  !event.currentTarget.contains(relatedTarget)
                ) {
                  setSocialsOpen(false);
                }
              }}
            >
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-green-900/70 transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                aria-expanded={socialsOpen}
                aria-haspopup="menu"
              >
                {t("landingPage.header.socials")}
                <ChevronDown className="size-4" />
              </button>
              <AnimatePresence>
                {socialsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.16 }}
                    className="absolute right-0 top-full z-20 mt-2 min-w-36 rounded-xl border border-green-100 bg-white px-2 py-1 shadow-lg"
                    role="menu"
                  >
                    {socialLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-lg px-3 py-2 text-xs uppercase text-green-900/70 transition-colors hover:bg-green-50 hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                        role="menuitem"
                      >
                        {link.name}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href={DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-wide text-green-900/70 transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              {t("landingPage.header.docs")}
            </a>

            <Link
              to="/home"
              className="rounded-full bg-[#111827] px-4 py-2 text-[11px] uppercase tracking-wide text-white transition-colors hover:bg-[#1f2937] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              {t("landingPage.header.launchApp")}
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={navVariants}
            className="flex items-center gap-2 md:hidden"
          >
            <Link
              to="/home"
              className="rounded-full bg-[#111827] px-3 py-2 text-[10px] uppercase tracking-wide text-white transition-colors hover:bg-[#1f2937] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              {t("landingPage.header.launchApp")}
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="rounded-lg p-1 text-[#111827] transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              aria-label={t("landingPage.header.openMenu")}
            >
              <Menu className="size-6" />
            </button>
          </motion.div>
        </div>
      </header>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="right"
          variant="shadcn"
          shouldShowCloseButton={false}
          className="w-full max-w-80 border-l border-green-200 bg-white! p-0"
          style={{ fontFamily: "'DeadRevolver', Helvetica, Arial, sans-serif" }}
        >
          <motion.div
            initial="hidden"
            animate={mobileMenuOpen ? "visible" : "hidden"}
            variants={createStaggerVariant(prefersReducedMotion, 0.08, 0.04)}
            className="flex flex-col gap-6 p-6"
          >
            <motion.div variants={bodyVariants} className="space-y-1">
              <motion.div
                variants={bodyVariants}
                className="text-xs uppercase tracking-wide text-[#6B7280]"
              >
                {t("landingPage.header.socials")}
              </motion.div>
              {socialLinks.map((link) => (
                <motion.a
                  key={link.name}
                  variants={bodyVariants}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg px-3 py-2 text-sm text-[#111827] transition-colors hover:bg-green-50"
                >
                  {link.name}
                </motion.a>
              ))}
            </motion.div>

            <motion.div variants={bodyVariants} className="space-y-1">
              <motion.div
                variants={bodyVariants}
                className="text-xs uppercase tracking-wide text-[#6B7280]"
              >
                {t("landingPage.header.resources")}
              </motion.div>
              <motion.a
                variants={bodyVariants}
                href={DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg px-3 py-2 text-sm text-[#111827] transition-colors hover:bg-green-50"
              >
                {t("landingPage.header.documentation")}
              </motion.a>
            </motion.div>

            <motion.div variants={buttonVariants}>
              <Link
                to="/home"
                onClick={() => setMobileMenuOpen(false)}
                className="mt-2 inline-flex w-full items-center justify-center rounded-full bg-[#111827] px-4 py-3 text-sm uppercase tracking-wide text-white transition-colors hover:bg-[#1f2937]"
              >
                {t("landingPage.header.launchApp")}
              </Link>
            </motion.div>
          </motion.div>
        </SheetContent>
      </Sheet>

      <main>
        <section className="relative overflow-hidden border-b border-black/5 bg-white">
          <UnicornScene
            // projectId="wzFwmpe4nX3prWa9djap"
            jsonFilePath="/assets/landing-page/glyph-wave.json"
            height="100vh"
            width="100%"
            className="absolute! min-h-screen border px-4 [&>a]:hidden!"
          />
          <div className="pointer-events-none absolute min-h-screen" />

          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroGroupVariants}
            className="relative z-10 mx-auto flex min-h-[720px] w-full max-w-[1440px] flex-col items-center justify-center px-4 py-20 text-center sm:px-8 md:px-12 lg:px-20"
          >
            <motion.a
              variants={badgeVariants}
              href="https://blog.colosseum.com/introducing-colosseum-accelerator-cohort-3"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-8 inline-flex items-center gap-2 rounded-full border-[0.5px] border-green-500 bg-[#DCFCE7] px-4 py-2 text-xs uppercase text-[#166534] shadow-[0_4px_16px_rgba(34,197,94,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <span>{t("landingPage.hero.badge")}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 1777 230"
                fill="black"
                className="w-auto h-3"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M236.57 0.30127H0V29.1277H236.57V0.30127ZM213.166 51.5044H23.3529V80.3307H213.166V51.5044ZM103.847 102.716H132.673V229.301H103.847V102.716ZM75.022 102.716H46.1957V229.301H75.022V102.716ZM161.524 102.716H190.35V229.301H161.524V102.716Z"
                  fill="currentColor"
                />
                <g clip-path="url(#clip0_1178_6146)">
                  <path
                    d="M387.601 64.8609C358.393 64.8609 336.252 81.8834 336.252 113.58C336.252 145.277 359.144 164.648 389.949 164.648C412.395 164.648 432.799 151.734 436.743 128.419C443.341 131.354 449.798 134.289 456.56 136.919C452.451 175.354 417.819 188.714 386.873 188.714C342.427 188.691 310.73 156.853 310.73 113.557C310.73 70.2611 343.014 41.5225 388.047 41.5225C421.2 41.5225 450.244 56.3379 457.147 88.3167L436.156 96.3936C429.7 74.5344 406.948 64.6965 387.601 64.6965V64.8374V64.8609Z"
                    fill="currentColor"
                  />
                  <path
                    d="M477.691 115.318C477.691 69.5334 506.899 40.3486 550.899 40.3486C594.899 40.3486 625 69.9795 625 113.862C625 157.745 595.651 188.832 551.486 188.832C507.322 188.832 477.55 159.342 477.55 115.318H477.691ZM551.04 63.0766C521.245 63.0766 502.321 82.0009 502.321 113.252C502.321 144.503 521.245 166.362 551.463 166.362C581.681 166.362 600.605 147.438 600.605 116.187C600.605 84.9358 581.234 63.0766 551.017 63.0766H551.04Z"
                    fill="currentColor"
                  />
                  <path
                    d="M776.253 184.136H656.392V45.6313H679.871V161.243H776.253V184.136Z"
                    fill="currentColor"
                  />
                  <path
                    d="M784.778 115.318C784.778 69.5334 813.963 40.3486 858.127 40.3486C902.292 40.3486 932.228 69.9795 932.228 113.862C932.228 157.745 902.879 188.832 858.714 188.832C814.55 188.832 784.778 159.342 784.778 115.318ZM858.127 63.0766C828.356 63.0766 809.408 82.0009 809.408 113.252C809.408 144.503 828.332 166.362 858.55 166.362C888.768 166.362 907.692 147.438 907.692 116.187C907.692 84.9358 888.322 63.0766 858.104 63.0766H858.127Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1100.78 140.417C1100.78 178.266 1062.35 189.278 1031.69 189.278C1008.35 189.278 967.563 185.897 959.651 148.494L980.477 138.515C989.282 161.69 1011.14 164.789 1029.2 164.789C1045.49 164.789 1074.98 161.408 1074.98 142.343C1074.98 106.701 960.402 146.146 960.402 86.1566C960.402 53.7317 998.697 40.959 1029.36 40.959C1060.02 40.959 1093.04 51.2194 1097.73 82.3295L1078.22 90.1012C1072.35 69.275 1049.91 62.6539 1029.36 62.6539C1009.99 62.6539 988.131 70.2846 988.131 83.9261C988.131 116.797 1100.95 81.8834 1100.95 140.558L1100.81 140.417H1100.78Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1263.8 140.417C1263.8 178.266 1225.22 189.278 1194.7 189.278C1171.39 189.278 1130.58 185.897 1122.67 148.494L1143.49 138.515C1152.3 161.69 1174.16 164.789 1192.21 164.789C1208.51 164.789 1238 161.408 1238 142.343C1238 106.701 1123.42 146.146 1123.42 86.1566C1123.42 53.7317 1161.71 40.959 1192.38 40.959C1223.04 40.959 1256.05 51.2194 1260.75 82.3295L1241.24 90.1012C1235.37 69.275 1212.92 62.6539 1192.38 62.6539C1173.01 62.6539 1151.15 70.2846 1151.15 83.9261C1151.15 116.797 1263.97 81.8834 1263.97 140.558L1263.82 140.417H1263.8Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1423.41 184.136H1293.85V45.6313H1419.45V68.9698H1317.19V101.089H1402.14V124.851H1317.19V161.243H1423.41V184.136Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1449.99 132.927V45.6313H1473.47V127.504C1473.47 151.852 1487.56 165.352 1517.19 165.352C1546.82 165.352 1560.91 151.852 1560.91 127.504V45.6313H1584.39V132.927C1584.39 166.385 1559.15 189.113 1517.19 189.113C1475.23 189.113 1449.99 166.362 1449.99 132.927Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1698.38 142.906L1747.1 45.6313H1776.87V184.136H1753.39V87.448C1743.86 105.339 1713.64 166.385 1704.09 184.277H1692.79L1643.65 87.448V183.995H1620.17V45.6313H1649.8C1659.33 63.9687 1689.13 124.71 1698.52 142.906H1698.38Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1178_6146">
                    <rect
                      width="1466.14"
                      height="148.906"
                      fill="white"
                      transform="translate(310.73 40.3486)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </motion.a>

            <motion.h1
              variants={heroGroupVariants}
              className="max-w-4xl text-[clamp(2.5rem,7vw,5.75rem)] uppercase leading-[0.7] text-[#15803D]"
            >
              {[
                t("landingPage.hero.headlineLine1"),
                t("landingPage.hero.headlineLine2"),
                t("landingPage.hero.headlineLine3"),
              ].map((line) => (
                <motion.div
                  key={line}
                  variants={heroLineVariants}
                  className="bg-radial-[at_50%_50%] from-white via-white to-transparent my-[clamp(0.5rem,0.75rem,1rem)]"
                  style={{
                    transform: "perspective(100vw) rotateX(23deg)",
                  }}
                >
                  <p
                    className="relative block text-shadow-[0_6px_18px_rgba(132,204,22,52%)]"
                    style={{
                      fontFamily:
                        "'DeadRevolverDisplayOutlined', Helvetica, Arial, sans-serif",
                    }}
                  >
                    {line}
                  </p>
                </motion.div>
              ))}
            </motion.h1>

            <motion.p
              variants={bodyVariants}
              className="mt-6 max-w-3xl text-[clamp(0.8rem,2vw,1.35rem)] uppercase tracking-wide text-[#166534]"
            >
              {t("landingPage.hero.subheadline")}
            </motion.p>

            <motion.div
              variants={buttonGroupVariants}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 "
            >
              <motion.a
                variants={bodyVariants}
                href="solanadappstore://details?id=fun.cfl.www.twa"
                className="inline-flex items-center h-12 sm:h-14"
              >
                <img
                  src="https://cdn.navalabs.io/assets/dapp-store-button_eA26KT.svg"
                  className="h-full"
                />
              </motion.a>
              <Popover>
                <PopoverTrigger asChild>
                  <motion.button
                    variants={bodyVariants}
                    className="inline-flex items-center h-12 sm:h-14 cursor-pointer"
                  >
                    <img
                      src="https://cdn.navalabs.io/assets/open-world-mini-app_eA26KT.svg"
                      className="h-full"
                    />
                  </motion.button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto bg-white! border border-[#e8e8e3] rounded-xl p-4 shadow-lg flex flex-col items-center gap-1"
                  style={{
                    borderImage: "none 100% 1 0 stretch",
                  }}
                  sideOffset={8}
                >
                  <img
                    src="/assets/landing-page/world-mini-app-qr.png"
                    alt="World Mini App QR Code"
                    className="w-48 h-48"
                  />
                  <a
                    href="https://world.org/mini-app?app_id=app_a131c09384e80f329db6e614d5070164"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-700 underline hover:text-green-900"
                  >
                    Or, open it in the app
                  </a>
                </PopoverContent>
              </Popover>
              <motion.a
                variants={bodyVariants}
                href="https://cdn.navalabs.io/app-release-signed-v3.0.7.apk"
                className="inline-flex items-center h-12 sm:h-14"
              >
                <img
                  src="https://cdn.navalabs.io/assets/apk-download-button_eA26KT.svg"
                  className="h-full"
                />
              </motion.a>
              <motion.div
                variants={bodyVariants}
                className="inline-flex items-center h-12 sm:h-14"
              >
                <Link to="/home" className="h-full">
                  <img
                    src="https://cdn.navalabs.io/assets/available-as-web-app_eA26KT.svg"
                    className="h-full"
                  />
                </Link>
              </motion.div>
            </motion.div>
            {/*<motion.div
              variants={buttonGroupVariants}
              className="mt-10 flex flex-col items-center gap-2 sm:gap-4 sm:flex-row"
            >
              <motion.div variants={buttonVariants}>
                <Link
                  to="/home"
                  className="inline-flex items-center justify-center rounded-full bg-green-700 px-7 py-3.25 text-xs uppercase tracking-wide text-white shadow-[0_4px_20px_rgba(34,197,94,0.35)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 md:px-9 md:py-4 md:text-sm"
                >
                  {t("landingPage.hero.primaryCta")}
                </Link>
              </motion.div>

              <motion.a
                variants={buttonVariants}
                href={DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full border border-[#e8e8e3] bg-white px-7 py-3.25 text-xs uppercase tracking-wide text-green-950/70 shadow-[0_4px_20px_rgba(232,232,227,0.35)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 md:px-9 md:py-4 md:text-sm"
              >
                {t("landingPage.hero.secondaryCta")}
              </motion.a>
            </motion.div>*/}
          </motion.div>
        </section>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={DEFAULT_VIEWPORT}
          variants={sectionGridVariants}
          className="bg-[#F0FDF4] py-12 sm:py-14"
        >
          <motion.div
            variants={sectionGridVariants}
            className="mx-auto grid w-full max-w-[1440px] grid-cols-2 gap-8 px-4 sm:px-8 md:grid-cols-4 md:px-12 lg:px-20"
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.label}
                variants={cardVariants}
                className="flex flex-col items-center justify-center text-center"
              >
                <HyperText
                  startOnView
                  className="text-[clamp(1.9rem,3.6vw,3rem)] leading-none text-[#15803D] tracking-tight! text-shadow-[0_0px_16px_rgba(132,204,22,52%)]"
                  style={{
                    fontFamily: "'DeadRevolver', Helvetica, Arial, sans-serif",
                  }}
                >
                  {stat.value}
                </HyperText>
                <motion.div
                  variants={bodyVariants}
                  className="mt-2 text-xs tracking-tight uppercase text-green-950/65 lg:text-base"
                >
                  {stat.label}
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={DEFAULT_VIEWPORT}
          className="relative flex items-center overflow-hidden bg-white py-20"
        >
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4 sm:px-8 md:px-12 lg:px-20">
            <motion.div variants={headingGroupVariants} className="text-center">
              <motion.h2
                variants={headingVariants}
                className="text-[clamp(2rem,4vw,3rem)] uppercase text-green-900"
                style={{
                  fontFamily:
                    "'DeadRevolverDisplay', Helvetica, Arial, sans-serif",
                }}
              >
                {t("landingPage.howToPlay.title")}
              </motion.h2>
              <motion.p
                variants={bodyVariants}
                className="text-sm uppercase text-[#166534] sm:text-base"
              >
                {t("landingPage.howToPlay.subtitle")}
              </motion.p>
            </motion.div>

            <div className="relative mt-16">
              <motion.div
                variants={sectionGridVariants}
                className="grid gap-6 lg:grid-cols-3"
              >
                {howToPlayCards.map((card, index) => (
                  <motion.article
                    key={card.id}
                    variants={cardVariants}
                    // className={`relative rounded-2xl border bg-white px-6 pb-8 pt-12 text-left ${card.borderClass}`}
                    className={`relative`}
                  >
                    {index !== howToPlayCards.length - 1 && (
                      // Desktop
                      <motion.div
                        variants={connectorVariants}
                        className="hidden lg:flex absolute -right-6 top-1/2 z-10 size-10 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-xl text-lime-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          viewBox="0 0 7 7"
                          shape-rendering="crispEdges"
                          className="size-10"
                        >
                          {CONNECTOR_RECTS.map((rect) => (
                            <motion.rect
                              key={`${rect.x}-${rect.y}`}
                              custom={rect.sequence}
                              variants={connectorRectVariants}
                              x={rect.x}
                              y={rect.y}
                              width="1"
                              height="1"
                              fill="currentColor"
                            />
                          ))}
                        </svg>
                      </motion.div>
                    )}

                    <motion.div
                      variants={cardContentVariants}
                      className="text-center"
                    >
                      <motion.div
                        variants={bodyVariants}
                        className="flex h-40 w-full items-center justify-center px-8"
                      >
                        <img
                          src={`/assets/landing-page/how-to-play-${index + 1}.svg`}
                          alt={`How to Play ${index + 1}`}
                          className="size-full"
                        />
                      </motion.div>

                      <motion.p
                        variants={headingVariants}
                        className="mt-5 text-xl tracking-tight uppercase text-green-900"
                      >
                        {card.title}
                      </motion.p>
                      <motion.p
                        variants={bodyVariants}
                        className="mt-2 text-green-950/65 tracking-tight"
                      >
                        {card.description}
                      </motion.p>

                      {index !== howToPlayCards.length - 1 && (
                        // Mobile
                        <motion.div
                          variants={connectorVariants}
                          className="flex lg:hidden mt-4 size-10 rotate-90 mx-auto items-center justify-center text-lime-800"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            version="1.1"
                            viewBox="0 0 7 7"
                            shape-rendering="crispEdges"
                            className="size-10"
                          >
                            {CONNECTOR_RECTS.map((rect) => (
                              <motion.rect
                                key={`${rect.x}-${rect.y}`}
                                custom={rect.sequence}
                                variants={connectorRectVariants}
                                x={rect.x}
                                y={rect.y}
                                width="1"
                                height="1"
                                fill="currentColor"
                              />
                            ))}
                          </svg>
                        </motion.div>
                      )}
                    </motion.div>
                  </motion.article>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={DEFAULT_VIEWPORT}
          className="relative bg-[#F0FDF4] py-20 sm:py-24"
        >
          <FlickeringGrid
            className="absolute h-full w-full inset-0 z-0 mask-[linear-gradient(to_bottom,transparent_10%,white_50%,transparent_90%)]"
            squareSize={8}
            gridGap={4}
            color="#22C55F"
            maxOpacity={0.5}
            flickerChance={0.1}
          />
          <div className="relative mx-auto w-full max-w-[1440px] px-4 sm:px-8 md:px-12 lg:px-20">
            <motion.div variants={headingGroupVariants} className="text-center">
              <motion.h2
                variants={headingVariants}
                className="text-[clamp(2rem,4vw,3rem)] uppercase text-green-900"
                style={{
                  fontFamily:
                    "'DeadRevolverDisplay', Helvetica, Arial, sans-serif",
                }}
              >
                {t("landingPage.gameModes.title")}
              </motion.h2>
              <motion.p
                variants={bodyVariants}
                className="text-sm uppercase text-[#166534] sm:text-base"
              >
                {t("landingPage.gameModes.subtitle")}
              </motion.p>
            </motion.div>

            <motion.div
              variants={sectionGridVariants}
              className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
            >
              {modes.map((mode) => (
                <motion.article
                  key={mode.title}
                  variants={cardVariants}
                  className={`relative overflow-hidden rounded-2xl ${mode.cardClass}`}
                >
                  <motion.img
                    variants={imageVariants}
                    src={mode.image}
                    alt={mode.imageAlt}
                    className="h-48 w-full object-cover [image-rendering:pixelated]"
                    loading="lazy"
                  />
                  <motion.div
                    variants={cardContentVariants}
                    className="px-4 pb-4 pt-3"
                  >
                    <motion.p
                      variants={headingVariants}
                      className="text-lg uppercase text-green-900 tracking-tight"
                    >
                      {mode.title}
                    </motion.p>
                    <motion.p
                      variants={bodyVariants}
                      className="mt-1 text-sm text-green-950/65"
                    >
                      {mode.description}
                    </motion.p>
                  </motion.div>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={DEFAULT_VIEWPORT}
          className="bg-white py-20 sm:py-24"
        >
          <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-8 md:px-12 lg:px-20">
            <motion.div variants={headingGroupVariants} className="text-center">
              <motion.h2
                variants={headingVariants}
                className="text-[clamp(2rem,4vw,3rem)] uppercase text-green-900"
                style={{
                  fontFamily:
                    "'DeadRevolverDisplay', Helvetica, Arial, sans-serif",
                }}
              >
                {t("landingPage.whyCfl.title")}
              </motion.h2>
              <motion.p
                variants={bodyVariants}
                className="text-sm uppercase text-[#166534] sm:text-base"
              >
                {t("landingPage.whyCfl.subtitle")}
              </motion.p>
            </motion.div>

            <motion.div
              variants={sectionGridVariants}
              className="mt-12 grid gap-6 lg:grid-cols-3"
            >
              {whyCflFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <motion.article
                    key={feature.title}
                    variants={cardVariants}
                    className="relative rounded-2xl border border-[#d8d8d0] bg-white px-6 py-7 shadow-[0_0_18px_rgba(132,204,22,0.2)] overflow-hidden"
                  >
                    <motion.div variants={cardContentVariants}>
                      <div className="absolute -bottom-8 -right-4 lg:-top-4 lg:-right-4">
                        <Icon className="size-48 lg:size-32 text-[#f4f4f0]/60" />
                        {/*{feature.icon}*/}
                      </div>
                      <motion.div
                        variants={bodyVariants}
                        className="flex items-center justify-start"
                      >
                        <Icon className="size-8 text-green-900" />
                        {/*{feature.icon}*/}
                      </motion.div>
                      <motion.h3
                        variants={headingVariants}
                        className="relative mt-5 text-lg text-green-900"
                        style={{
                          fontFamily:
                            "'DeadRevolver', Helvetica, Arial, sans-serif",
                        }}
                      >
                        {feature.title}
                      </motion.h3>
                      <motion.p
                        variants={bodyVariants}
                        className="relative mt-2 text-sm leading-relaxed text-green-950/65"
                      >
                        {feature.description}
                      </motion.p>
                    </motion.div>
                  </motion.article>
                );
              })}
            </motion.div>
          </div>
        </motion.section>

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={DEFAULT_VIEWPORT}
          className="relative overflow-hidden bg-emerald-950"
        >
          <div className="pointer-events-none absolute z-10 min-h-screen min-w-full">
            <RetroGrid
              lightLineColor="#22C55E"
              darkLineColor="#22C55E"
              angle={90}
              cellSize={50}
            />
          </div>

          <motion.div
            variants={heroGroupVariants}
            className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-col items-center px-4 py-20 text-center sm:px-8 md:px-12 lg:px-20"
          >
            <motion.h2
              variants={heroGroupVariants}
              className="relative text-[clamp(2.5rem,6vw,5.75rem)] uppercase leading-[0.8] text-green-100  z-10 bg-radial-[at_50%_50%] from-green-950 via-green-950 to-transparent to-75%"
              style={{
                fontFamily:
                  "'DeadRevolverDisplay', Helvetica, Arial, sans-serif",
              }}
            >
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-60 w-[60vw] max-w-145 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#22C55E]/20 blur-2xl" />
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-85 w-[88vw] max-w-215 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#22C55E]/20 blur-3xl" />
              <motion.span
                variants={heroLineVariants}
                className="block relative z-10"
                style={{
                  transform: "perspective(100vw) rotateX(30deg)",
                }}
              >
                {t("landingPage.cta.titleLine1")}
              </motion.span>
              <motion.span
                variants={heroLineVariants}
                className="block relative z-10"
                style={{
                  transform: "perspective(100vw) rotateX(33deg)",
                }}
              >
                {t("landingPage.cta.titleLine2")}
              </motion.span>
            </motion.h2>

            <motion.p
              variants={bodyVariants}
              className="mt-5 max-w-2xl text-sm text-white sm:text-base"
            >
              {t("landingPage.cta.subtitle")}
            </motion.p>

            <motion.div
              variants={buttonGroupVariants}
              className="mt-8 flex flex-col items-center gap-4 sm:flex-row"
            >
              <motion.div variants={buttonVariants}>
                <Link
                  to="/home"
                  className="inline-flex min-w-44 items-center justify-center rounded-full bg-[#22C55E] px-8 py-3 text-sm text-[#111827] shadow-[0_8px_30px_rgba(34,197,94,0.45)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                >
                  {t("landingPage.cta.primaryCta")}
                </Link>
              </motion.div>
              <motion.a
                variants={buttonVariants}
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-w-44 items-center justify-center rounded-full border border-[#374151] bg-[#02291C] px-8 py-3 text-sm text-white transition-colors hover:bg-[#1F2937] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              >
                {t("landingPage.cta.secondaryCta")}
              </motion.a>
            </motion.div>
          </motion.div>
        </motion.section>
      </main>

      <motion.footer
        initial="hidden"
        whileInView="visible"
        viewport={DEFAULT_VIEWPORT}
        variants={createStaggerVariant(prefersReducedMotion, 0.12, 0.04)}
        className="bg-white"
      >
        <div className="h-[3px] w-full bg-lime-500" />

        <div className="mx-auto w-full max-w-[1440px] px-4 pb-8 pt-12 sm:px-8 md:px-12 lg:px-20">
          <motion.div
            variants={createStaggerVariant(prefersReducedMotion, 0.12, 0.08)}
            className="flex flex-col justify-between lg:flex-row"
          >
            <motion.div
              variants={footerColumnVariants}
              className="mb-4 lg:mb-0 max-w-sm"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              >
                <img
                  src={CFL_LOGO_URL}
                  alt={t("landingPage.cflLogo")}
                  className="h-7 w-auto object-contain"
                />
              </Link>
              <motion.p
                variants={bodyVariants}
                className="mt-4 text-sm text-[#6B7280]"
              >
                {t("landingPage.footer.tagline")}
              </motion.p>
              <motion.div
                variants={buttonGroupVariants}
                className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4"
              >
                <motion.a
                  variants={bodyVariants}
                  href="solanadappstore://details?id=fun.cfl.www.twa"
                  className="inline-flex items-center w-full h-fit"
                >
                  <img
                    src="https://cdn.navalabs.io/assets/dapp-store-button_eA26KT.svg"
                    className="h-full"
                  />
                </motion.a>
                <Popover>
                  <PopoverTrigger asChild>
                    <motion.button
                      variants={bodyVariants}
                      className="inline-flex items-center w-full h-fit cursor-pointer"
                    >
                      <img
                        src="https://cdn.navalabs.io/assets/open-world-mini-app_eA26KT.svg"
                        className="h-full"
                      />
                    </motion.button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto bg-white! border border-[#e8e8e3] rounded-xl p-4 shadow-lg flex flex-col items-center gap-1"
                    style={{
                      borderImage: "none 100% 1 0 stretch",
                    }}
                    sideOffset={8}
                  >
                    <img
                      src="/assets/landing-page/world-mini-app-qr.png"
                      alt="World Mini App QR Code"
                      className="w-48 h-48"
                    />
                    <a
                      href="https://world.org/mini-app?app_id=app_a131c09384e80f329db6e614d5070164"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-green-700 underline hover:text-green-900"
                    >
                      Or, open it in the app
                    </a>
                  </PopoverContent>
                </Popover>
                <motion.a
                  variants={bodyVariants}
                  href="https://cdn.navalabs.io/app-release-signed-v3.0.7.apk"
                  className="inline-flex items-center w-full h-fit"
                >
                  <img
                    src="https://cdn.navalabs.io/assets/apk-download-button_eA26KT.svg"
                    className="h-full"
                  />
                </motion.a>
                <motion.a
                  variants={bodyVariants}
                  href="/home"
                  className="inline-flex items-center w-full h-fit"
                >
                  <img
                    src="https://cdn.navalabs.io/assets/available-as-web-app_eA26KT.svg"
                    className="h-full"
                  />
                </motion.a>
              </motion.div>
            </motion.div>

            <motion.div
              variants={createStaggerVariant(prefersReducedMotion, 0.1, 0.06)}
              className="grid grid-cols-2 gap-10 sm:flex sm:gap-16"
            >
              <motion.div variants={footerColumnVariants}>
                <motion.h3
                  variants={headingVariants}
                  className="text-sm uppercase tracking-wide text-[#111827]"
                  style={{
                    fontFamily:
                      "'DeadRevolverDisplay', Helvetica, Arial, sans-serif",
                  }}
                >
                  {t("landingPage.footer.legal")}
                </motion.h3>
                <motion.div
                  variants={createStaggerVariant(
                    prefersReducedMotion,
                    0.06,
                    0.04,
                  )}
                  className="mt-3 space-y-2 text-sm text-[#6B7280]"
                >
                  <motion.div variants={bodyVariants}>
                    <Link
                      to="/terms-of-service"
                      className="block transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                    >
                      {t("landingPage.footer.termsOfService")}
                    </Link>
                  </motion.div>
                  <motion.div variants={bodyVariants}>
                    <Link
                      to="/privacy-policy"
                      className="block transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                    >
                      {t("landingPage.footer.privacyPolicy")}
                    </Link>
                  </motion.div>
                  <motion.div variants={bodyVariants}>
                    <Link
                      to="/responsible-gaming-policy"
                      className="block transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                    >
                      {t("landingPage.footer.responsibleGaming")}
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.div>

              <motion.div variants={footerColumnVariants}>
                <motion.h3
                  variants={headingVariants}
                  className="text-sm uppercase tracking-wide text-[#111827]"
                  style={{
                    fontFamily:
                      "'DeadRevolverDisplay', Helvetica, Arial, sans-serif",
                  }}
                >
                  {t("landingPage.footer.officialLinks")}
                </motion.h3>
                <motion.div
                  variants={createStaggerVariant(
                    prefersReducedMotion,
                    0.06,
                    0.04,
                  )}
                  className="mt-3 space-y-2 text-sm text-[#6B7280]"
                >
                  <motion.a
                    variants={bodyVariants}
                    href={X_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                  >
                    {t("landingPage.footer.xTwitter")}
                  </motion.a>
                  <motion.a
                    variants={bodyVariants}
                    href={DISCORD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                  >
                    {t("landingPage.footer.discord")}
                  </motion.a>
                  <motion.a
                    variants={bodyVariants}
                    href={DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                  >
                    {t("landingPage.footer.docs")}
                  </motion.a>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            variants={footerColumnVariants}
            className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-[#E5E7EB] pt-6 text-xs text-[#9CA3AF] sm:flex-row sm:items-center"
          >
            <motion.span variants={bodyVariants}>
              {t("landingPage.footer.copyright")}
            </motion.span>
            {/*
            <motion.span
              variants={bodyVariants}
              className="flex items-center gap-2"
            >
              {t("landingPage.footer.builtOn")}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                style={{
                  color: "currentColor",
                }}
                viewBox="0 0 149 22"
                className="block h-4!"
              >
                <mask
                  id="kw92m5ht"
                  width="149"
                  height="23"
                  x="0"
                  y="0"
                  maskUnits="userSpaceOnUse"
                  style={{
                    maskType: "luminance",
                  }}
                >
                  <path fill="#fff" d="M0 0h149v22.142H0z"></path>
                </mask>
                <g mask="url(#kw92m5ht)">
                  <path
                    fill="url(#pn47x3vj)"
                    d="m25.033 17.458-4.087 4.382a.95.95 0 0 1-.692.302H.879a.476.476 0 0 1-.348-.798l4.082-4.382a.95.95 0 0 1 .692-.302H24.68a.473.473 0 0 1 .353.798m-4.087-8.827a.96.96 0 0 0-.692-.302H.879a.475.475 0 0 0-.348.798l4.082 4.385a.96.96 0 0 0 .692.302H24.68a.476.476 0 0 0 .346-.798zM.879 5.483h19.375a.95.95 0 0 0 .692-.302L25.033.798a.475.475 0 0 0-.09-.724A.47.47 0 0 0 24.68 0H5.305a.95.95 0 0 0-.692.302L.531 4.685a.475.475 0 0 0 .348.798"
                  ></path>
                  <path
                    fill="currentColor"
                    d="M48.653 9.365H38.288V5.95h13.06V2.538H38.252a3.407 3.407 0 0 0-3.425 3.388v3.46a3.406 3.406 0 0 0 3.425 3.392h10.38v3.414H35.075v3.413h13.578a3.41 3.41 0 0 0 3.425-3.388v-3.46a3.406 3.406 0 0 0-3.425-3.392m20.08-6.827H58.33a3.407 3.407 0 0 0-3.434 3.388v10.291a3.405 3.405 0 0 0 3.434 3.389h10.405a3.407 3.407 0 0 0 3.425-3.389V5.926a3.4 3.4 0 0 0-2.12-3.136 3.4 3.4 0 0 0-1.305-.252Zm-.025 13.654H58.354V5.952h10.35zm36.468-13.654H95.028a3.407 3.407 0 0 0-3.425 3.388v13.68h3.46v-5.607h10.102v5.607h3.46V5.926a3.41 3.41 0 0 0-2.136-3.143 3.4 3.4 0 0 0-1.313-.246Zm-.025 8.047H95.049V5.951h10.102zm40.424-8.047h-10.149a3.406 3.406 0 0 0-3.425 3.388v13.68h3.46v-5.607h10.079v5.607H149V5.926a3.42 3.42 0 0 0-1.011-2.403 3.4 3.4 0 0 0-2.414-.985m-.035 8.047h-10.102V5.951h10.102zm-20.066 5.607h-1.384l-4.948-12.224a2.27 2.27 0 0 0-2.113-1.43h-3.07a2.27 2.27 0 0 0-2.283 2.26v14.808h3.46V5.95h1.384l4.945 12.225a2.285 2.285 0 0 0 2.122 1.42h3.07a2.27 2.27 0 0 0 2.283-2.26V2.538h-3.466zm-46.8-13.654h-3.46v13.68a3.405 3.405 0 0 0 3.438 3.388H89.03v-3.414H78.675z"
                  ></path>
                </g>
                <defs>
                  <linearGradient
                    id="pn47x3vj"
                    x1="2.494"
                    x2="22.809"
                    y1="22.671"
                    y2="-0.233"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0.08" stop-color="#9945FF"></stop>
                    <stop offset="0.3" stop-color="#8752F3"></stop>
                    <stop offset="0.5" stop-color="#5497D5"></stop>
                    <stop offset="0.6" stop-color="#43B4CA"></stop>
                    <stop offset="0.72" stop-color="#28E0B9"></stop>
                    <stop offset="0.97" stop-color="#19FB9B"></stop>
                  </linearGradient>
                </defs>
              </svg>
            </motion.span>
            */}
          </motion.div>
        </div>
      </motion.footer>
    </div>
  );
}
